import type { EventRecord } from '@opensentry/shared';
import { SEVERITY_RANK, SOURCE_IDS } from '@opensentry/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { timeAgo } from '../lib/format.js';
import { CategoryIcon } from './CategoryIcon.js';
import { SeverityPill } from './SeverityPill.js';
import { Drawer } from './ui/Drawer.js';

function rank(e: EventRecord) {
  return SEVERITY_RANK[e.severity] * 1e12 + e.occurredAt;
}

/** Floating ranked list of the most pressing events. Click → event drawer. */
export function TopThreatsCard({
  events,
  onEventClick,
}: {
  events: EventRecord[];
  onEventClick: (id: number) => void;
}) {
  const top = useMemo(() => [...events].sort((a, b) => rank(b) - rank(a)).slice(0, 40), [events]);
  if (top.length === 0) return null;

  return (
    <div className="pointer-events-auto flex min-h-0 w-full flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]/90 p-3 shadow-lg backdrop-blur">
      <div className="mb-2 shrink-0 text-xs font-semibold text-[var(--color-muted)]">
        Top threats
      </div>
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto" style={{ maxHeight: '50vh' }}>
        {top.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => onEventClick(e.id)}
              className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-[var(--color-panel-2)]"
            >
              <CategoryIcon category={e.category} size={13} />
              <SeverityPill severity={e.severity} />
              <span className="flex-1 truncate">{e.title}</span>
              <span className="mono shrink-0 text-[10px] text-[var(--color-muted)]">
                {timeAgo(e.occurredAt)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Source freshness pills; click opens a left drawer with that source's
 *  recent events, filterable by region. */
export function SourceStrip({
  events,
  lastUpdated,
}: {
  events: EventRecord[];
  lastUpdated: Record<string, number | null>;
}) {
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [region, setRegion] = useState<string>('all');
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: (s: string) => api.triggerIngest(s),
    onSuccess: () => qc.invalidateQueries(),
  });

  const srcEvents = events.filter((e) => e.source === openSource);
  const regions = [...new Set(srcEvents.map((e) => e.countryCode).filter(Boolean))].sort();
  const shown = region === 'all' ? srcEvents : srcEvents.filter((e) => e.countryCode === region);

  return (
    <>
      <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5">
        {SOURCE_IDS.map((s) => {
          const ts = lastUpdated[s];
          return (
            <button
              key={s}
              type="button"
              onClick={() => {
                setOpenSource(s);
                setRegion('all');
              }}
              className="flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-panel)]/90 px-2.5 py-1 text-[10px] shadow backdrop-blur hover:bg-[var(--color-panel-2)]"
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: ts ? '#3f8f5e' : '#5b6470' }}
              />
              <span className="uppercase">{s}</span>
              <span className="mono text-[var(--color-muted)]">{ts ? timeAgo(ts) : 'never'}</span>
            </button>
          );
        })}
      </div>

      <Drawer
        open={openSource != null}
        onClose={() => setOpenSource(null)}
        side="left"
        title={
          <span className="flex items-center gap-2">
            <span className="uppercase">{openSource}</span>
            <span className="mono text-[11px] text-[var(--color-muted)]">
              {openSource && lastUpdated[openSource]
                ? `updated ${timeAgo(lastUpdated[openSource] as number)}`
                : 'no ingest yet'}
            </span>
          </span>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] px-2 py-1 text-xs"
            >
              <option value="all">All regions ({srcEvents.length})</option>
              {regions.map((r) => (
                <option key={r} value={r as string}>
                  {r}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={run.isPending}
              onClick={() => openSource && run.mutate(openSource)}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-panel-2)]"
            >
              <RefreshCw size={11} className={run.isPending ? 'animate-spin' : ''} />
              Run now
            </button>
          </div>
          <ul className="space-y-2">
            {shown.slice(0, 100).map((e) => (
              <li
                key={e.id}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-2"
              >
                <div className="flex items-center gap-2">
                  <CategoryIcon category={e.category} size={13} />
                  <SeverityPill severity={e.severity} />
                  <span className="ml-auto mono text-[10px] text-[var(--color-muted)]">
                    {timeAgo(e.occurredAt)}
                  </span>
                </div>
                <div className="mt-1 text-xs">{e.title}</div>
              </li>
            ))}
            {shown.length === 0 && (
              <li className="text-xs text-[var(--color-muted)]">
                No recent events from this source in range.
              </li>
            )}
          </ul>
        </div>
      </Drawer>
    </>
  );
}
